import { useQuery } from 'convex/react';
import { api } from '@aeon-karaoke/cloud-convex';
import { Input } from './Input';
import { Field } from './Field';

interface HandleFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** The handle the user holds now. Re-entering it shows `hint`, not a check. */
  currentHandle?: string;
  /** Resting copy under the field, e.g. the 30-day rule. */
  hint?: string;
}

/** Whether a HandleField value can be submitted. */
export function isHandleSubmittable(status: HandleStatus) {
  return status.kind === 'available' || status.kind === 'unchanged';
}

export type HandleStatus =
  | { kind: 'unchanged' }
  | { kind: 'short' }
  | { kind: 'checking' }
  | { kind: 'available'; handle: string }
  | { kind: 'taken' }
  | { kind: 'invalid'; reason: string };

/** Live availability from users.checkHandle for anything three characters
 * or longer that is not the handle already held. */
export function useHandleStatus(value: string, currentHandle?: string): HandleStatus {
  const trimmed = value.trim().toLowerCase();
  const unchanged = currentHandle !== undefined && trimmed === currentHandle;
  const check = useQuery(api.users.checkHandle, !unchanged && trimmed.length >= 3 ? { handle: trimmed } : 'skip');
  if (unchanged) return { kind: 'unchanged' };
  if (trimmed.length < 3) return { kind: 'short' };
  if (check === undefined) return { kind: 'checking' };
  if (check.status === 'available') return { kind: 'available', handle: trimmed };
  if (check.status === 'taken') return { kind: 'taken' };
  return { kind: 'invalid', reason: check.reason };
}

interface HintLine {
  text?: string;
  tone: 'faint' | 'up' | 'down';
  border?: string;
}

function hintLine(status: HandleStatus, hint?: string): HintLine {
  switch (status.kind) {
    case 'unchanged':
    case 'short':
      return { text: hint, tone: 'faint' };
    case 'checking':
      return { text: 'Checking…', tone: 'faint' };
    case 'available':
      return { text: `@${status.handle} is available`, tone: 'up', border: 'border-up' };
    case 'taken':
      return { text: 'That handle is taken', tone: 'down', border: 'border-down' };
    case 'invalid':
      return { text: status.reason, tone: 'down', border: 'border-down' };
  }
}

/** Handle input with a green border and "@x is available" on success. */
export function HandleField({ value, onChange, currentHandle, hint }: HandleFieldProps) {
  const line = hintLine(useHandleStatus(value, currentHandle), hint);
  return (
    <Field label="Handle" hint={line.text} hintTone={line.tone}>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={line.border}
      />
    </Field>
  );
}
